import type { AppNotificationsApi, NotificationPermission, ScheduledNotification } from '@yada/types';
import { isAppNotificationEnabled } from '@yada/types';

const STORAGE_KEY = 'yada:notifications:scheduled';
const HISTORY_KEY = 'yada:notifications:history';
const HISTORY_MAX = 50; // keep last 50 notifications

interface PersistedEntry {
  appId: string;
  notification: ScheduledNotification;
  /** Scheduled fire time as unix ms. Stored for restoration after page reload. */
  firesAt: number;
}

export interface NotificationHistoryEntry {
  appId: string;
  notification: ScheduledNotification;
  /** When the notification actually fired (unix ms) */
  firedAt: number;
  /** Whether the user has seen/read it */
  read: boolean;
}

/**
 * NotificationManager — owns scheduling, cancellation, and permission for all apps.
 *
 * Phase 1: local-only, fires via setTimeout + Web Notifications API.
 * Phase 2 (push): service worker push events will also route through onActivate listeners.
 *
 * One instance is created by CustomRuntime and shared across all apps.
 * Each app gets a scoped API via createApi(appId).
 */
export class NotificationManager {
  private timers = new Map<string, ReturnType<typeof setTimeout>>();
  private activateListeners = new Map<string, Set<(n: ScheduledNotification) => void>>();
  private historyListeners = new Set<() => void>();

  // ── Permission ──────────────────────────────────────────────────────────────

  get permission(): NotificationPermission {
    if (typeof Notification === 'undefined') return 'denied';
    return Notification.permission as NotificationPermission;
  }

  async requestPermission(): Promise<NotificationPermission> {
    if (typeof Notification === 'undefined') return 'denied';
    const result = await Notification.requestPermission();
    return result as NotificationPermission;
  }

  // ── Scheduling ──────────────────────────────────────────────────────────────

  async schedule(appId: string, notification: ScheduledNotification): Promise<void> {
    // Idempotent: cancel any existing with the same id
    await this.cancel(appId, notification.id);
    // The preference is enforced here, not only in each app, so an app that
    // forgets to check cannot notify a user who never opted in.
    if (!isAppNotificationEnabled(appId)) return;

    const delay = notification.scheduledFor
      ? Math.max(0, notification.scheduledFor.getTime() - Date.now())
      : 0;

    // Persist so we can restore across page reloads (only future notifications)
    if (notification.scheduledFor && delay > 0) {
      this.persist(appId, notification, notification.scheduledFor.getTime());
    }

    const timerKey = `${appId}:${notification.id}`;
    const timer = setTimeout(() => {
      this.timers.delete(timerKey);
      this.removePersisted(appId, notification.id);
      this.fire(appId, notification);
    }, delay);

    this.timers.set(timerKey, timer);
  }

  async cancel(appId: string, id: string): Promise<void> {
    const timerKey = `${appId}:${id}`;
    const timer = this.timers.get(timerKey);
    if (timer !== undefined) {
      clearTimeout(timer);
      this.timers.delete(timerKey);
    }
    this.removePersisted(appId, id);
  }

  async cancelAll(appId: string): Promise<void> {
    for (const key of [...this.timers.keys()]) {
      if (key.startsWith(`${appId}:`)) {
        clearTimeout(this.timers.get(key));
        this.timers.delete(key);
      }
    }
    this.removeAllPersisted(appId);
  }

  // ── Activation listener ─────────────────────────────────────────────────────

  onActivate(appId: string, cb: (n: ScheduledNotification) => void): () => void {
    if (!this.activateListeners.has(appId)) {
      this.activateListeners.set(appId, new Set());
    }
    this.activateListeners.get(appId)!.add(cb);
    return () => this.activateListeners.get(appId)?.delete(cb);
  }

  /**
   * Programmatically trigger onActivate callbacks for an app's notification
   * (e.g. when clicked in the NotificationHistoryPanel).
   */
  triggerActivate(appId: string, notification: ScheduledNotification): void {
    const listeners = this.activateListeners.get(appId);
    if (listeners) {
      for (const cb of listeners) {
        try {
          cb(notification);
        } catch (e) {
          console.warn('[NotificationManager] Error in onActivate listener:', e);
        }
      }
    }
  }

  // ── Restoration ─────────────────────────────────────────────────────────────

  /**
   * Re-register any persisted future notifications.
   * Call once at app startup (before CustomRuntime activates any app).
   */
  restore(): void {
    const entries = this.getStored();
    const now = Date.now();
    for (const entry of entries) {
      if (entry.firesAt > now) {
        // Re-schedule with corrected delay
        this.schedule(entry.appId, {
          ...entry.notification,
          scheduledFor: new Date(entry.firesAt),
        });
      }
      // Past notifications are silently dropped — the moment has passed
    }
  }

  // ── History API ─────────────────────────────────────────────────────────────

  /** Returns a copy of the full notification history, newest first. */
  getHistory(): NotificationHistoryEntry[] {
    return this.loadHistory().slice().reverse();
  }

  /** Count of unread (fired but not yet opened in the notification panel) notifications. */
  get unreadCount(): number {
    return this.loadHistory().filter(e => !e.read).length;
  }

  /** Mark all notifications as read. */
  markAllRead(): void {
    const history = this.loadHistory().map(e => ({ ...e, read: true }));
    this.saveHistory(history);
    this.notifyHistoryListeners();
  }

  /** Clear all notification history. Supports user right to delete their own data. */
  clearHistory(): void {
    this.saveHistory([]);
    this.notifyHistoryListeners();
  }

  /** Subscribe to history changes (new notification fired or read state changed). */
  onHistoryChange(cb: () => void): () => void {
    this.historyListeners.add(cb);
    return () => this.historyListeners.delete(cb);
  }

  // ── Per-app API factory ─────────────────────────────────────────────────────

  createApi(appId: string): AppNotificationsApi {
    return {
      get permission(): NotificationPermission {
        if (typeof Notification === 'undefined') return 'denied';
        return Notification.permission as NotificationPermission;
      },
      requestPermission: () => this.requestPermission(),
      schedule: (n) => this.schedule(appId, n),
      cancel: (id) => this.cancel(appId, id),
      cancelAll: () => this.cancelAll(appId),
      onActivate: (cb) => this.onActivate(appId, cb),
    };
  }

  // ── Private ─────────────────────────────────────────────────────────────────

  private fire(appId: string, notification: ScheduledNotification): void {
    // Record in history so users can always see what fired
    this.addToHistory(appId, notification);

    // Show browser notification if permission granted
    if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
      try {
        const n = new Notification(notification.title, {
          body: notification.body,
          data: { appId, notificationId: notification.id, url: '/', ...(notification.data as object ?? {}) },
        });
        n.onclick = () => {
          window.focus();
          n.close();
          this.dispatchActivate(appId, notification);
        };
      } catch (err) {
        console.warn('[NotificationManager] Failed to show notification:', err);
      }
    }

    // Always dispatch to in-app listeners (useful when app is in foreground)
    this.dispatchActivate(appId, notification);
  }

  private dispatchActivate(appId: string, notification: ScheduledNotification): void {
    this.activateListeners.get(appId)?.forEach(cb => cb(notification));
  }

  private addToHistory(appId: string, notification: ScheduledNotification): void {
    const history = this.loadHistory();
    // Deduplicate by notification id + appId
    const filtered = history.filter(
      e => !(e.appId === appId && e.notification.id === notification.id)
    );
    filtered.push({ appId, notification, firedAt: Date.now(), read: false });
    // Keep only the last HISTORY_MAX entries
    const trimmed = filtered.slice(-HISTORY_MAX);
    this.saveHistory(trimmed);
    this.notifyHistoryListeners();
  }

  private notifyHistoryListeners(): void {
    this.historyListeners.forEach(cb => cb());
  }

  private persist(appId: string, notification: ScheduledNotification, firesAt: number): void {
    const entries = this.getStored().filter(
      e => !(e.appId === appId && e.notification.id === notification.id)
    );
    entries.push({ appId, notification, firesAt });
    this.saveStored(entries);
  }

  private removePersisted(appId: string, id: string): void {
    const entries = this.getStored().filter(
      e => !(e.appId === appId && e.notification.id === id)
    );
    this.saveStored(entries);
  }

  private removeAllPersisted(appId: string): void {
    this.saveStored(this.getStored().filter(e => e.appId !== appId));
  }

  private getStored(): PersistedEntry[] {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '[]');
    } catch {
      return [];
    }
  }

  private saveStored(entries: PersistedEntry[]): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
    } catch {
      // Ignore storage quota errors
    }
  }

  private loadHistory(): NotificationHistoryEntry[] {
    try {
      return JSON.parse(localStorage.getItem(HISTORY_KEY) ?? '[]');
    } catch {
      return [];
    }
  }

  private saveHistory(entries: NotificationHistoryEntry[]): void {
    try {
      localStorage.setItem(HISTORY_KEY, JSON.stringify(entries));
    } catch {
      // Ignore storage quota errors
    }
  }
}
